import { getPermissionCodes, hasPermissionCode } from '../../helpers/permissionHelper';
import { PROFILE_PERMISSION_CODES } from './permissionCodes';
import { Profile } from './types';

function hasPermission(profile: Profile | null | undefined, permissionCode: string): boolean {
    return hasPermissionCode(getPermissionCodes(profile), permissionCode);
}

function hasAnyPermission(profile: Profile | null | undefined, permissionCodes: string[]): boolean {
    const profilePermissionCodes = getPermissionCodes(profile);

    return permissionCodes.some((permissionCode) => hasPermissionCode(profilePermissionCodes, permissionCode));
}

function hasAllPermissions(profile: Profile | null | undefined, permissionCodes: string[]): boolean {
    const profilePermissionCodes = getPermissionCodes(profile);

    return permissionCodes.every((permissionCode) => hasPermissionCode(profilePermissionCodes, permissionCode));
}

export function canManageProjects(profile: Profile | null | undefined): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.EDIT_PROJECTS);
}

export function canManageProjectClient(profile: Profile | null | undefined, ...unusedArgs: unknown[]): boolean {
    void unusedArgs;

    return hasPermission(profile, PROFILE_PERMISSION_CODES.EDIT_PROJECTS)
        && hasAnyPermission(profile, [
            PROFILE_PERMISSION_CODES.SHOW_CLIENTS,
            PROFILE_PERMISSION_CODES.EDIT_CLIENTS,
        ]);
}

export function canManageProjectManager(profile: Profile | null | undefined, ...unusedArgs: unknown[]): boolean {
    void unusedArgs;

    return hasAllPermissions(profile, [
        PROFILE_PERMISSION_CODES.EDIT_PROJECTS,
        PROFILE_PERMISSION_CODES.EDIT_EMPLOYEES,
    ]);
}

export function canManageProjectEmployees(profile: Profile | null | undefined, ...unusedArgs: unknown[]): boolean {
    void unusedArgs;

    return hasAllPermissions(profile, [
        PROFILE_PERMISSION_CODES.EDIT_PROJECTS,
        PROFILE_PERMISSION_CODES.EDIT_EMPLOYEES,
    ]);
}

export function canManageProjectRoleForNotification(profile: Profile | null | undefined, ...unusedArgs: unknown[]): boolean {
    void unusedArgs;

    return hasAllPermissions(profile, [
        PROFILE_PERMISSION_CODES.EDIT_PROJECTS,
        PROFILE_PERMISSION_CODES.EDIT_ROLES,
    ]);
}

export function canManageProjectType(
    profile: Profile | null | undefined,
): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.EDIT_PROJECTS)
        && hasAnyPermission(profile, [
            PROFILE_PERMISSION_CODES.SHOW_PROJECT_TYPES,
            PROFILE_PERMISSION_CODES.EDIT_PROJECT_TYPES,
        ]);
}

export function canManageRoles(profile: Profile | null | undefined): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.EDIT_ROLES);
}

export function canManageRoleEmployees(profile: Profile | null | undefined, ...unusedArgs: unknown[]): boolean {
    void unusedArgs;

    return hasAllPermissions(profile, [
        PROFILE_PERMISSION_CODES.EDIT_ROLES,
        PROFILE_PERMISSION_CODES.EDIT_EMPLOYEES,
        PROFILE_PERMISSION_CODES.CHANGE_EMPLOYEE_ROLES,
    ]);
}

export function canManageRolePermissions(profile: Profile | null | undefined, ...unusedArgs: unknown[]): boolean {
    void unusedArgs;

    return hasAllPermissions(profile, [
        PROFILE_PERMISSION_CODES.EDIT_ROLES,
        PROFILE_PERMISSION_CODES.CHANGE_PERMISSIONS,
    ]);
}

export function canManageEmployees(profile: Profile | null | undefined): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.EDIT_EMPLOYEES);
}

export function canManageEmployeeRoles(profile: Profile | null | undefined, ...unusedArgs: unknown[]): boolean {
    void unusedArgs;

    return hasAllPermissions(profile, [
        PROFILE_PERMISSION_CODES.EDIT_EMPLOYEES,
        PROFILE_PERMISSION_CODES.EDIT_ROLES,
        PROFILE_PERMISSION_CODES.CHANGE_EMPLOYEE_ROLES,
    ]);
}

export function canManageEmployeeProjects(profile: Profile | null | undefined, ...unusedArgs: unknown[]): boolean {
    void unusedArgs;

    return hasAllPermissions(profile, [
        PROFILE_PERMISSION_CODES.EDIT_EMPLOYEES,
        PROFILE_PERMISSION_CODES.EDIT_PROJECTS,
    ]);
}

// The server only applies an Admin-flag change when the caller themselves is an admin
// (see EmployeesController.IsAdminAsync check), regardless of EditEmployees. `canEditAdminLabel`
// must come from the backend (EmployeeDto.canEditAdminLabel / EmployeePageDto.canEditAdminLabel)
// for the currently logged-in user, so the checkbox is never shown when the server would ignore it.
export function canManageEmployeeAdminLabel(
    profile: Profile | null | undefined,
    canEditAdminLabel?: boolean,
): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.EDIT_EMPLOYEES) && Boolean(canEditAdminLabel);
}

export function canSeeWorkCalendars(profile: Profile | null | undefined): boolean {
    return hasAnyPermission(profile, [
        PROFILE_PERMISSION_CODES.SHOW_WORK_CALENDARS,
        PROFILE_PERMISSION_CODES.EDIT_WORK_DAYS,
    ]);
}

export function canSeeOwnWorkCalendar(profile: Profile | null | undefined): boolean {
    return Boolean(profile?.id);
}

export function canManageEmployeeWorkCalendar(
    profile: Profile | null | undefined,
): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.EDIT_EMPLOYEES)
        && canSeeWorkCalendars(profile);
}

export function canManageTimezone(profile: Profile | null | undefined): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.EDIT_TIMEZONE);
}

export function canManageJobExecutionTime(profile: Profile | null | undefined): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.EDIT_JOB_EXECUTION_TIME);
}

export function canManageWorkCalendars(profile: Profile | null | undefined): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.EDIT_WORK_DAYS);
}

export function canManageIntegrations(profile: Profile | null | undefined): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.EDIT_INTEGRATIONS);
}

export function canManageEarlyAccess(profile: Profile | null | undefined): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.EDIT_EARLY_ACCESS);
}

export function canSeeTimesheetPage(profile: Profile | null | undefined): boolean {
    return Boolean(profile?.id);
}

export function canSeeTimeReportPage(profile: Profile | null | undefined): boolean {
    return canSeeTimesheetPage(profile);
}

export function canSeeOtherTimeReportItems(profile: Profile | null | undefined): boolean {
    return hasAnyPermission(profile, [
        PROFILE_PERMISSION_CODES.ACCESS_TO_TIMESHEET,
        PROFILE_PERMISSION_CODES.SHOW_SUMMARY_FOR_ALL_EMPLOYEES,
    ]);
}

export function canSeeVacationReportPage(profile: Profile | null | undefined): boolean {
    return Boolean(profile?.id);
}

export function canSeeOtherVacations(profile: Profile | null | undefined): boolean {
    return Boolean(profile?.isAdmin)
        || hasAnyPermission(profile, [
            PROFILE_PERMISSION_CODES.SHOW_ALL_VACATIONS,
            PROFILE_PERMISSION_CODES.EDIT_VACATION_WITHOUT_APPROVAL,
            PROFILE_PERMISSION_CODES.APPROVE_DECLINE_ANY_VACATIONS,
        ]);
}

export function canApproveDeclineAnyVacations(profile: Profile | null | undefined): boolean {
    return Boolean(profile?.isAdmin)
        || hasPermission(profile, PROFILE_PERMISSION_CODES.APPROVE_DECLINE_ANY_VACATIONS);
}

export function canEditVacationWithoutApproval(profile: Profile | null | undefined): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.EDIT_VACATION_WITHOUT_APPROVAL);
}

export function canManageOwnTimesheetItems(profile: Profile | null | undefined): boolean {
    return Boolean(profile?.id);
}

export function canSeeOtherTimesheetItems(profile: Profile | null | undefined): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.ACCESS_TO_TIMESHEET);
}

export function canManageTimesheetItems(profile: Profile | null | undefined): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.EDIT_SHIFTS);
}

export function canManageTimesheetInternalNote(profile: Profile | null | undefined): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.EDIT_SHIFTS);
}

export function canViewTimesheetInternalNote(profile: Profile | null | undefined): boolean {
    return hasAnyPermission(profile, [
        PROFILE_PERMISSION_CODES.ACCESS_TO_TIMESHEET,
        PROFILE_PERMISSION_CODES.EDIT_SHIFTS,
    ]);
}

export function canManageOldTimesheetItems(profile: Profile | null | undefined): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.EDIT_OLD_SHIFTS);
}

export function canApproveTimesheetItems(profile: Profile | null | undefined): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.CAN_APPROVE);
}

export function canDisapproveTimesheetItems(profile: Profile | null | undefined): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.CAN_DISAPPROVE);
}

export function canBillTimesheetItems(profile: Profile | null | undefined): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.CAN_BILL);
}

export function canUnbillTimesheetItems(profile: Profile | null | undefined): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.CAN_UNBILL);
}

export function canImportTimeTracking(profile: Profile | null | undefined): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.IMPORT_TIME_TRACKING);
}

export function canSeeCostReportPage(profile: Profile | null | undefined): boolean {
    return Boolean(profile?.id);
}

export function canManageOwnCosts(profile: Profile | null | undefined): boolean {
    return Boolean(profile?.id);
}

export function canSeeOtherCosts(profile: Profile | null | undefined): boolean {
    return hasAnyPermission(profile, [
        PROFILE_PERMISSION_CODES.SHOW_COSTS_FOR_ALL_EMPLOYEES,
        PROFILE_PERMISSION_CODES.SHOW_AND_EDIT_COST_FOR_ALL_EMPLOYEES,
    ]);
}

export function canManageOtherCosts(profile: Profile | null | undefined): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.SHOW_AND_EDIT_COST_FOR_ALL_EMPLOYEES);
}

export function canSeeTimeBankReportPage(profile: Profile | null | undefined): boolean {
    return Boolean(profile?.id);
}

export function canManageOwnTimeBank(profile: Profile | null | undefined): boolean {
    return Boolean(profile?.id);
}

export function canSeeOtherTimeBank(profile: Profile | null | undefined): boolean {
    return hasAnyPermission(profile, [
        PROFILE_PERMISSION_CODES.SHOW_TIME_BANK_FOR_ALL_EMPLOYEES,
        PROFILE_PERMISSION_CODES.SHOW_AND_EDIT_TIME_BANK_FOR_ALL_EMPLOYEES,
    ]);
}

export function canManageOtherTimeBank(profile: Profile | null | undefined): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.SHOW_AND_EDIT_TIME_BANK_FOR_ALL_EMPLOYEES);
}

export function canManageClients(profile: Profile | null | undefined): boolean {
    return hasPermission(profile, PROFILE_PERMISSION_CODES.EDIT_CLIENTS);
}

export function canSeeProjectTypesSection(
    profile: Profile | null | undefined,
    ...unusedArgs: unknown[]
): boolean {
    void unusedArgs;

    return hasAnyPermission(profile, [
        PROFILE_PERMISSION_CODES.SHOW_PROJECT_TYPES,
        PROFILE_PERMISSION_CODES.EDIT_PROJECT_TYPES,
    ]);
}

export function canManageProjectTypes(
    profile: Profile | null | undefined,
    ...unusedArgs: unknown[]
): boolean {
    void unusedArgs;

    return hasPermission(profile, PROFILE_PERMISSION_CODES.EDIT_PROJECT_TYPES);
}

export function canSeeShiftTypesSection(
    profile: Profile | null | undefined,
    ...unusedArgs: unknown[]
): boolean {
    void unusedArgs;

    return hasAnyPermission(profile, [
        PROFILE_PERMISSION_CODES.SHOW_SHIFT_TYPES,
        PROFILE_PERMISSION_CODES.EDIT_SHIFT_TYPES,
    ]);
}

export function canManageShiftTypes(
    profile: Profile | null | undefined,
    ...unusedArgs: unknown[]
): boolean {
    void unusedArgs;

    return hasPermission(profile, PROFILE_PERMISSION_CODES.EDIT_SHIFT_TYPES);
}

export function canSeeAbsenceTypesSection(
    profile: Profile | null | undefined,
    ...unusedArgs: unknown[]
): boolean {
    void unusedArgs;

    return hasAnyPermission(profile, [
        PROFILE_PERMISSION_CODES.SHOW_ABSENCE_TYPES,
        PROFILE_PERMISSION_CODES.EDIT_ABSENCE_TYPES,
    ]);
}

export function canManageAbsenceTypes(
    profile: Profile | null | undefined,
    ...unusedArgs: unknown[]
): boolean {
    void unusedArgs;

    return hasPermission(profile, PROFILE_PERMISSION_CODES.EDIT_ABSENCE_TYPES);
}

export function canSeeTagTypesSection(
    profile: Profile | null | undefined,
    ...unusedArgs: unknown[]
): boolean {
    void unusedArgs;

    return hasAnyPermission(profile, [
        PROFILE_PERMISSION_CODES.SHOW_TAG_TYPES,
        PROFILE_PERMISSION_CODES.EDIT_TAG_TYPES,
    ]);
}

export function canManageTagTypes(
    profile: Profile | null | undefined,
    ...unusedArgs: unknown[]
): boolean {
    void unusedArgs;

    return hasPermission(profile, PROFILE_PERMISSION_CODES.EDIT_TAG_TYPES);
}
